/**
 * The whiteboard: the room's answer to its own question, at the front of the
 * room, rewritten as evidence lands.
 *
 * A room's description IS its question, and nothing ever went back and answered
 * it — the reader inferred an answer from eight tables. This is the line they
 * watch instead: it takes a stance immediately, says how little is behind it,
 * sharpens, and sometimes says it was wrong. A person stops digging when it
 * stops moving and they like where it landed.
 *
 * THE HONESTY IS THE FEATURE. The strength chip and the reason it is not
 * stronger read as ONE sentence, because a rung shown alone is a claim and a
 * rung shown with its count is evidence.
 *
 * TWO SIZES. This is the line; the reasons, the counter-case, what it rubbed
 * out and every version are in the answer's slideout (`AnswerBrief`). They used
 * to open underneath it, and pushed the tables the answer rests on off the
 * bottom of the screen at the moment the reader wanted both in view.
 */
import type { BoardroomAnswer, BoardroomAnswering } from '../../../types';
import {
  answerDoorLabel,
  answererNote,
  cappedNote,
  isReversal,
  movementLine,
  strengthTone,
  strengthWords,
  supportWords,
} from './answerStance';
import HeaderRow, { Door } from './HeaderRow';

const since = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

export default function AnswerLine({
  answer,
  history,
  answering,
  turnsDone,
  question,
  onOpen,
  lead,
}: {
  answer: BoardroomAnswer | null;
  history: BoardroomAnswer[];
  answering?: BoardroomAnswering;
  turnsDone: number;
  /** The room's question — its description. Said right above the answer to it:
   *  it used to sit in the title row as grey small print, and the page laid out
   *  a question and its answer as two unrelated things. */
  question?: string;
  /** Open the answer's slideout. The whole row is the door. */
  onOpen?: () => void;
  /** The lead the room follows: the answer is that lead's own. */
  lead?: string | null;
}) {
  // WHY IT IS NOT ANSWERING. The reason was on the run and nothing read it, so a
  // parked answerer looked exactly like one that had not started yet.
  const parked = answererNote(answering);
  const parkedLine = parked && (
    <span
      className={parked.tone === 'bad' ? 'text-amber-700 dark:text-amber-300' : 'text-gray-400'}
      data-testid="answer-line-parked"
    >
      {parked.text}
    </span>
  );

  const asked = (question ?? '').trim();
  const questionLine = asked && (
    <div className="truncate text-xs text-gray-500 dark:text-gray-400" title={asked} data-testid="answer-question">
      {asked}
      {lead && <span className="text-indigo-600 dark:text-indigo-300"> · following {lead}</span>}
    </div>
  );

  if (!answer) {
    /* NOT AN EMPTY BOX. Before the first pass there is genuinely no answer, and
       saying so — with what it is waiting for — is more use than a skeleton. */
    return (
      <HeaderRow label="Answer" onOpen={onOpen} testId="answer-line">
        {questionLine}
        <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs text-gray-400">
          <span className="italic">
            {answering?.working
              ? 'working out where this lands…'
              : lead
                ? 'no answer for this lead yet · the next dig while following it writes one'
                : 'no answer yet · the room answers its own question once a few findings land'}
          </span>
          {parkedLine && <span>· {parkedLine}</span>}
          {onOpen && (
            <span className="text-xs">
              · <Door testId="answer-open">{answerDoorLabel(history)}</Door>
            </span>
          )}
        </div>
      </HeaderRow>
    );
  }

  const capped = cappedNote(answer);
  const ruled = isReversal(answer) ? answer.ruled_out : [];

  return (
    <HeaderRow label="Answer" onOpen={onOpen} testId="answer-line">
      {questionLine}
      {/* THE STANCE IS THE BIGGEST THING ON THE PAGE. Everything else in the
          room is what it rests on. */}
      <div
        className={`${asked ? 'mt-0.5 ' : ''}text-sm font-medium leading-snug text-gray-900 dark:text-gray-100`}
        data-testid="answer-stance"
      >
        {answer.stance}
      </div>

      {/* A REVERSAL IS THE ONE THING THAT MUST NOT BE MISSED: a reader who skims
          past it still believes the answer this one overturned. So it sits
          directly under the stance it replaced, open or not — but as ONE line:
          every earlier stance it overturns, with every reason, is the slideout's. */}
      {ruled.length > 0 && (
        <div
          className="mt-1 truncate rounded border border-amber-300 bg-amber-50/60 px-2 py-0.5 text-xs text-gray-800 dark:border-amber-700 dark:bg-amber-900/10 dark:text-gray-200"
          title={ruled.map(r => `${r.stance}${r.why ? ` - ${r.why}` : ''}`).join('\n')}
          data-testid="answer-ruled-out"
        >
          <span className="font-medium">changed from:</span>{' '}
          <span className="line-through opacity-70">{ruled[0].stance || 'the answer before this one'}</span>
          {ruled[0].why && <span className="text-gray-600 dark:text-gray-300"> - {ruled[0].why}</span>}
          {ruled.length > 1 && <span className="text-gray-500"> · and {ruled.length - 1} more</span>}
        </div>
      )}

      <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-xs">
        <span className={`rounded-full px-1.5 py-px ${strengthTone(answer.strength)}`} data-testid="answer-strength">
          {strengthWords(answer.strength)}
        </span>
        {/* Why it is not stronger, in the same breath — never a tooltip. */}
        {capped && (
          <span className="text-gray-500 dark:text-gray-400" data-testid="answer-capped">
            {capped}
          </span>
        )}
        {!capped && supportWords(answer) && (
          <span className="text-gray-400" data-testid="answer-support">
            {supportWords(answer)}
          </span>
        )}
        {/* WHAT IT DID, AND HOW LONG AGO — one phrase, because "changed its
            mind" beside "has not moved in 3 turns" read as a contradiction
            when it was simply a reversal three turns old. */}
        <span
          className="text-gray-400"
          data-testid="answer-movement"
          title={answer.created_at ? since(answer.created_at) : undefined}
        >
          · {movementLine(answer, turnsDone, { working: answering?.working })}
        </span>
        {parkedLine && <span>· {parkedLine}</span>}
        {/* The door at the end of the row's own details, not pinned to the far
            edge a screen's width away. The whole row opens; this says where. */}
        {onOpen && (
          <span className="text-gray-400">
            · <Door testId="answer-open">{answerDoorLabel(history)}</Door>
          </span>
        )}
      </div>
      {/* WHAT MOVED IT gets its own line. It is a whole sentence, and strung into
          the grey middot chain above it buried both itself and everything else. */}
      {answer.moved_by && (
        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400" data-testid="answer-moved-by">
          {answer.moved_by}
        </div>
      )}
    </HeaderRow>
  );
}
